<template>
    <Card class="tw-shadow-md">
        <p slot="title" class="tw-flex tw-items-center tw-gap-2">
            <Icon type="ios-list-box" size="18" />
            <span class="tw-font-semibold">Collection Officers Data</span>
            <Tag color="blue" class="tw-ml-2">{{ dataCount }} COs</Tag>
        </p>
        <div slot="extra">
            <Tag v-if="loading" color="warning">
                <Icon type="ios-loading" class="spin-icon" />Loading...
            </Tag>
            <Tag v-else color="success">
                <Icon type="ios-checkmark-circle" />Ready
            </Tag>
        </div>
        
        <Table 
            :columns="tableColumns" 
            :data="data" 
            size="small" 
            :loading="loading" 
            max-height="600" 
            stripe 
            border 
            ref="table" 
            class="enhanced-table"
            @on-expand="handleExpand"
        >
            <template slot-scope="{ row }" slot="requested_amount">
                <span :class="{ 'tw-font-bold tw-text-lg': row.co_code === 'TOTAL' }">
                    {{ formatNumber(row.requested_amount) }}
                </span>
            </template>
            
            <template slot-scope="{ row }" slot="actions">
                <div v-if="row.co_code !== 'TOTAL'" class="tw-flex tw-justify-center tw-gap-2">
                    <Tooltip content="Clear by CO (Credit Officer) clear money" placement="top">
                        <Button 
                            type="success" 
                            size="small" 
                            icon="md-checkmark-circle-outline" 
                            @click="$emit('clear-co', row)" 
                            ghost
                        >
                            Clear CO
                        </Button>
                    </Tooltip>
                </div>
            </template>
            
            <template slot-scope="{ row }" slot="disbursed_amount">
                <span :class="{ 'tw-font-bold tw-text-lg': row.co_code === 'TOTAL' }">
                    {{ formatNumber(row.disbursed_amount) }}
                </span>
            </template>
            
            <template slot-scope="{ row }" slot="returned_amount">
                <span :class="{ 
                    'tw-font-bold tw-text-lg': row.co_code === 'TOTAL', 
                    'tw-text-red-600': row.returned_amount < 0 
                }">
                    {{ formatNumber(row.returned_amount) }}
                </span>
            </template>
            
            <template slot-scope="{ row }" slot="actual_disbursed">
                <span :class="{ 'tw-font-bold tw-text-lg': row.co_code === 'TOTAL' }">
                    {{ formatNumber(row.actual_disbursed) }}
                </span>
            </template>
            
            <template slot-scope="{ row }" slot="disbursed_collected">
                <span :class="{ 'tw-font-bold tw-text-lg': row.co_code === 'TOTAL' }">
                    {{ formatNumber(row.disbursed_collected) }}
                </span>
            </template>
            
            <template slot-scope="{ row }" slot="actual_collected">
                <span :class="{ 'tw-font-bold tw-text-lg': row.co_code === 'TOTAL' }">
                    {{ formatNumber(row.actual_collected) }}
                </span>
            </template>
        </Table>
    </Card>
</template>

<script>
import RepaymentDetailsRow from './RepaymentDetailsRow.vue';

export default {
    name: 'CODataTable',
    components: {
        RepaymentDetailsRow
    },
    props: {
        data: {
            type: Array,
            required: true
        },
        dataCount: {
            type: Number,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        repaymentDetails: {
            type: Object,
            default: () => ({})
        },
        loadingRepayments: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        tableColumns() {
            return [
                {
                    type: 'expand',
                    fixed: "left",
                    width: 50,
                    render: (h, params) => {
                        if (params.row.co_code === 'TOTAL') return h('span');
                        return this.renderExpandedRow(h, params);
                    }
                },
                { 
                    title: "Branch", 
                    key: "branch_code", 
                    fixed: "left", 
                    width: 100, 
                    align: "center", 
                    className: "tw-bg-gray-50", 
                    render: (h, params) => params.row.co_code === "TOTAL" 
                        ? h("span", { class: "tw-font-bold tw-text-blue-600 tw-text-base" }, "TOTAL") 
                        : h("Tag", { props: { color: "blue" } }, params.row.branch_code) 
                },
                { 
                    title: "CO Name", 
                    key: "co_name", 
                    fixed: "left", 
                    width: 220, 
                    className: "tw-bg-gray-50", 
                    render: (h, params) => params.row.co_code === "TOTAL" 
                        ? h("span", { class: "tw-text-gray-600 tw-italic tw-text-sm" }, params.row.count_info || "") 
                        : h("div", { class: "tw-font-medium" }, [
                            h("div", params.row.co_name), 
                            h("div", { class: "tw-text-xs tw-text-gray-500" }, params.row.co_code)
                        ]) 
                },
                { 
                    title: "DETAIL TRANSACTIONS", 
                    align: "center", 
                    children: [
                        { title: "Loan Requested", slot: "requested_amount", width: 160, align: "right", className: "tw-bg-yellow-50" },
                        { title: "Disbursement", slot: "disbursed_amount", width: 140, align: "right" },
                        { title: "Returned", slot: "returned_amount", width: 130, align: "right" },
                        { title: "Actual Disbursed", slot: "actual_disbursed", width: 150, align: "right" },
                        { title: "Disburse Collected", slot: "disbursed_collected", width: 150, align: "right" },
                        { title: "Actual Collected", slot: "actual_collected", width: 150, align: "right" }
                    ]
                },
                { 
                    title: "CASH", 
                    align: "center", 
                    children: [
                        { 
                            title: "KHR", 
                            key: "cash_khr", 
                            width: 130, 
                            align: "right", 
                            className: "tw-bg-orange-50", 
                            render: (h, params) => h("span", { 
                                class: { 
                                    "tw-font-bold tw-text-lg": params.row.co_code === "TOTAL", 
                                    "tw-text-orange-600 tw-font-semibold": params.row.co_code !== "TOTAL" && params.row.cash_khr > 0 
                                } 
                            }, this.formatNumber(params.row.cash_khr, "៛")) 
                        },
                        { 
                            title: "USD", 
                            key: "cash_usd", 
                            width: 120, 
                            align: "right", 
                            className: "tw-bg-orange-50", 
                            render: (h, params) => h("span", { 
                                class: { 
                                    "tw-font-bold tw-text-lg": params.row.co_code === "TOTAL", 
                                    "tw-text-orange-600 tw-font-semibold": params.row.co_code !== "TOTAL" && params.row.cash_usd > 0 
                                } 
                            }, this.formatNumber(params.row.cash_usd, "$")) 
                        }
                    ]
                },
                { 
                    title: "ABA BANK", 
                    align: "center", 
                    children: [
                        { 
                            title: "KHR", 
                            key: "aba_khr", 
                            width: 130, 
                            align: "right", 
                            render: (h, params) => h("span", { 
                                class: { "tw-font-bold tw-text-lg": params.row.co_code === "TOTAL" } 
                            }, this.formatNumber(params.row.aba_khr, "៛")) 
                        },
                        { 
                            title: "USD", 
                            key: "aba_usd", 
                            width: 120, 
                            align: "right", 
                            render: (h, params) => h("span", { 
                                class: { "tw-font-bold tw-text-lg": params.row.co_code === "TOTAL" } 
                            }, this.formatNumber(params.row.aba_usd, "$")) 
                        }
                    ]
                },
                { 
                    title: "ACLEDA BANK", 
                    align: "center", 
                    children: [
                        { 
                            title: "KHR", 
                            key: "acleda_khr", 
                            width: 130, 
                            align: "right", 
                            render: (h, params) => h("span", { 
                                class: { "tw-font-bold tw-text-lg": params.row.co_code === "TOTAL" } 
                            }, this.formatNumber(params.row.acleda_khr, "៛")) 
                        },
                        { 
                            title: "USD", 
                            key: "acleda_usd", 
                            width: 120, 
                            align: "right", 
                            render: (h, params) => h("span", { 
                                class: { "tw-font-bold tw-text-lg": params.row.co_code === "TOTAL" } 
                            }, this.formatNumber(params.row.acleda_usd, "$")) 
                        }
                    ]
                },
                { 
                    title: "Actions", 
                    slot: "actions", 
                    width: 120, 
                    align: "center", 
                    fixed: "right", 
                    className: "tw-bg-gray-50" 
                }
            ];
        }
    },
    methods: {
        formatNumber(num, currency = "") {
            if ((!num && num !== 0) || num === 0) return currency ? `${currency} -` : "-";
            let val = new Intl.NumberFormat("en-US", { 
                minimumFractionDigits: currency === "$" ? 2 : 0, 
                maximumFractionDigits: 2 
            }).format(num);
            return currency ? `${currency} ${val}` : val;
        },
        
        handleExpand(row, status) {
            const coId = row.co_id;
            
            // If collapsing (status is false), clear the data
            if (!status) {
                this.$emit('clear-repayments', coId);
                return;
            }
            
            // If expanding and data not loaded, fetch it
            if (status && !this.repaymentDetails[coId] && !this.loadingRepayments[coId]) {
                this.$emit('fetch-repayments', row);
            }
        },
        
        renderExpandedRow(h, params) {
            const coId = params.row.co_id;
            const details = this.repaymentDetails[coId];
            const isLoading = this.loadingRepayments[coId];
            
            return h(RepaymentDetailsRow, {
                props: {
                    details: details,
                    loading: isLoading
                }
            });
        }
    }
};
</script>

<style scoped>
.enhanced-table ::v-deep .ivu-table-header th { 
    background-color: #f8f9fa; 
    font-weight: 600; 
    color: #2c3e50; 
}
.enhanced-table ::v-deep .ivu-table-row:hover td { 
    background-color: #f0f9ff !important; 
}
.spin-icon { 
    animation: spin 1s linear infinite; 
}
@keyframes spin { 
    from { transform: rotate(0deg); } 
    to { transform: rotate(360deg); } 
}
</style>
